import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Phone, Star } from "lucide-react";
import Seo from "@/components/Seo";
import LegacyRedirect from "@/components/LegacyRedirect";
import QuoteForm from "@/components/QuoteForm";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { FadeUp, Eyebrow, EASE } from "@/components/Reveal";
import { getServiceBySlug, SERVICES } from "@/data/services";
import { SITE, PROCESS_STEPS } from "@/data/site";

function Faq({ faqs }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white" data-testid="service-faqs">
      {faqs.map((f, i) => (
        <div key={i}>
          <button
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
            data-testid={`service-faq-${i}`}
          >
            <span className="font-display text-sm font-bold text-slate-900 sm:text-base">{f.q}</span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className={`h-4 w-4 shrink-0 text-brand-red transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          <motion.div
            initial={false}
            animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-slate-600">{f.a}</p>
          </motion.div>
        </div>
      ))}
    </div>
  );
}

export default function ServicePage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const [projectRef, setProjectRef] = useState("");

  const enquireAbout = (title) => {
    setProjectRef(`Hi, I'd like a quote for something like your "${title}" project. `);
    const target = document.getElementById("service-quote");
    if (window.__lenis) window.__lenis.scrollTo(target, { offset: -90 });
    else target?.scrollIntoView({ behavior: "smooth" });
  };

  if (!service) return <LegacyRedirect />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${service.title}`,
      serviceType: service.name,
      description: service.metaDescription,
      url: `${SITE.domain}/services/${service.slug}`,
      areaServed: { "@type": "City", name: "Sydney" },
      provider: {
        "@type": "LocalBusiness",
        name: "Fusion Glass",
        telephone: "+61485032854",
        url: SITE.domain,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: service.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <main data-testid="service-page">
      <Seo title={service.seoTitle} description={service.metaDescription} path={`/services/${service.slug}`} jsonLd={jsonLd} />

      {/* Header + spotlight image */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-paper to-white pt-12 pb-16 md:pt-16 md:pb-24">
        <div className="container-x relative grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Eyebrow className="mb-6 inline-flex items-center gap-3">
              <span className="h-px w-10 bg-brand-red" /> Fusion Glass - Sydney
            </Eyebrow>
            <h1 className="font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              {service.title}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7, ease: EASE }}
              className="mt-5 font-display text-lg font-bold text-brand-red"
            >
              {service.tagline}
            </motion.p>
            {service.intro.map((p, i) => (
              <FadeUp key={i} delay={0.35 + i * 0.1}>
                <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">{p}</p>
              </FadeUp>
            ))}
            <FadeUp delay={0.55}>
              <div className="mt-9 flex flex-wrap gap-4">
                <a href="#service-quote" className="btn-red" data-testid="service-quote-btn">
                  Request a free quote
                </a>
                <a href={`tel:${SITE.phoneMain}`} className="btn-outline" data-testid="service-call-btn">
                  <Phone className="h-4 w-4 text-brand-red" /> {SITE.phoneMainDisplay}
                </a>
              </div>
            </FadeUp>
            <FadeUp delay={0.65}>
              <div className="mt-8 flex items-center gap-3">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-xs text-slate-600">
                  <span className="font-bold text-slate-900">5.0</span> from 27 Google reviews
                </p>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-5">
            <FadeUp delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-3 rounded-[2rem] border-2 border-brand-red/15" aria-hidden="true" />
                <div className="relative overflow-hidden rounded-3xl shadow-[0_30px_70px_rgba(16,24,40,0.2)]">
                  <img src={service.image} alt={service.alt} style={service.imagePosition ? { objectPosition: service.imagePosition } : undefined} className="h-[380px] w-full object-cover lg:h-[480px]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 via-transparent to-transparent" />
                  <span className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-red backdrop-blur-md">
                    {service.badge}
                  </span>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-slate-100 bg-brand-paper py-16 md:py-20">
        <div className="container-x">
          <Eyebrow className="mb-8 block">What's included</Eyebrow>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {service.features.map((f, i) => (
              <FadeUp key={f} delay={Math.min(i * 0.05, 0.3)}>
                <div className="flex h-full items-start gap-3 rounded-xl border border-slate-100 bg-white p-4 transition-colors hover:border-brand-red/40">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                  <span className="text-sm font-medium text-slate-800">{f}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-20">
        <div className="container-x">
          <Eyebrow className="mb-8 block">How it works</Eyebrow>
          <div className="grid gap-4 sm:grid-cols-3">
            {PROCESS_STEPS.map((p, i) => (
              <FadeUp key={p.step} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-slate-100 bg-white p-6 transition-shadow hover:shadow-[0_16px_40px_rgba(16,24,40,0.08)]">
                  <p className="font-mono text-3xl font-bold text-brand-red/30">{p.step}</p>
                  <h3 className="mt-3 font-display text-base font-extrabold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Featured projects */}
      {(service.projects || (service.project ? [service.project] : [])).map((project, pi) => (
        <section key={pi} className="border-y border-slate-100 bg-brand-paper py-16 md:py-20" data-testid={`featured-project-${pi}`}>
          <div className="container-x">
            <Eyebrow className="mb-4 block">Featured project</Eyebrow>
            <FadeUp>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                {project.title}
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">{project.description}</p>
              <button
                type="button"
                onClick={() => enquireAbout(project.title)}
                className="group mt-6 inline-flex items-center gap-2 rounded-full border-2 border-brand-red bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-brand-red transition-colors duration-300 hover:bg-brand-red hover:text-white"
                data-testid={`project-${pi}-enquire-btn`}
              >
                Want something like this?
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </FadeUp>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {[].concat(project.beforeAfter || []).map((ba, bi) => (
                <FadeUp key={ba.after} delay={bi * 0.08}>
                  <BeforeAfterSlider {...ba} alt={project.title} />
                </FadeUp>
              ))}
              {project.video && (
                <FadeUp delay={0.15}>
                  <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_36px_rgba(16,24,40,0.08)]" data-testid={`project-${pi}-video`}>
                    <video
                      src={project.video.src}
                      poster={project.video.poster}
                      className="aspect-[3/4] w-full bg-slate-900 object-cover"
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      aria-label={`${project.title} - video`}
                    />
                    <figcaption className="p-4 text-xs leading-relaxed text-slate-500">{project.video.caption}</figcaption>
                  </figure>
                </FadeUp>
              )}
              {(project.photos || []).map((p, i) => (
                <FadeUp key={p.src} delay={0.1 + Math.min(i * 0.08, 0.3)} className={p.wide ? "md:col-span-3" : undefined}>
                  <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_36px_rgba(16,24,40,0.08)]" data-testid={`project-${pi}-photo-${i}`}>
                    <img src={p.src} alt={`${p.caption} - ${project.title}`} loading="lazy" className={`${p.wide ? "aspect-video" : "aspect-[3/4]"} w-full object-cover`} />
                    <figcaption className="p-4 text-xs leading-relaxed text-slate-500">{p.caption}</figcaption>
                  </figure>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* FAQ + quote */}
      <section className="pb-20 md:pb-28" id="service-quote">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Eyebrow className="mb-6 block">Common questions</Eyebrow>
            <Faq faqs={service.faqs} />
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(16,24,40,0.12)] sm:p-8" data-testid="service-quote-form">
              <h2 className="mb-6 font-display text-xl font-extrabold text-slate-900">
                Get a free quote for {service.name.toLowerCase()}
              </h2>
              <QuoteForm defaultService={service.name} defaultMessage={projectRef} />
            </div>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="border-t border-slate-100 bg-brand-paper py-14">
        <div className="container-x">
          <Eyebrow className="mb-8 block">Explore our other services</Eyebrow>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white p-4 transition-colors hover:border-brand-red/50"
                data-testid={`related-service-${s.slug}`}
              >
                <span className="text-sm font-semibold text-slate-800">{s.name}</span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-400 transition-all group-hover:text-brand-red" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
